import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ReferCard from './ReferCard';
import HowItWorks from './HowItWorks';
import FAQsSection from './FAQSection';
import ReferralActions from './ReferralActions';
import { getFAQs, getReferralLink } from '../../auth/_requests';
import { useAuth } from '../../auth/Auth';
import { FAQResponse } from '../../helpers/utils/types';
import SkeletonFAQItem from '../../helpers/FAQItem/SkeltonFAQItem';
import { showToast } from '../../helpers/utils/showToast';
import partyPopperIcon from '../../assets/pages/rewards/partyPopper.svg';
import chevronRightIcon from '../../assets/pages/rewards/chevronRight.svg';

const ReferAndEarn = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [faqs, setFaqs] = useState<FAQResponse[]>([]);
  const [faqLoading, setFAQLoading] = useState(true);
  useEffect(() => {
    const fetchReferCode = async () => {
      try {
        const response = await getReferralLink(auth?.data.token);
        setCode(response);
      } catch {
        showToast({
          message: 'Failed to fetch referral code',
          type: 'error',
        });
        console.error('Error fetching referral code for the user');
      }
    };
    fetchReferCode();
  }, []);

  useEffect(() => {
    const getReferralFAQs = async () => {
      try {
        setFAQLoading(true);
        const response = await getFAQs(auth?.data?.token, 'referral');
        if (response) {
          setFAQLoading(false);
          setFaqs(response);
        } else {
          setFAQLoading(false);
          setFaqs([]);
        }
      } catch (error) {
        setFAQLoading(false);
        console.error('Error fetching referral FAQs:', error);
      } finally {
        setFAQLoading(false);
      }
    };
    getReferralFAQs();
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      console.error('Failed to copy');
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: -5 }} // Starts slightly above
      animate={{ opacity: 1, y: 0 }} // Slides down to position
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50/70 to-white px-4 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-amber-100 text-orange-600">
          <img src={partyPopperIcon} alt="" className="h-7 w-7" aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-orange-700">
            Thanksgiving special
          </p>
          <p className="text-sm font-semibold leading-5 text-slate-900">
            We’ve doubled your referral rewards until Nov 10.
          </p>
        </div>

        <button
          type="button"
          aria-label="View referral rewards"
          onClick={() => navigate('/rewards/referrals')}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-orange-600 transition-colors hover:bg-amber-100"
        >
          <img src={chevronRightIcon} alt="" className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className='flex flex-col xxl:flex-row gap-5 w-full'>
        {/* Left Column */}
        <div className='flex-[6] w-full p-12 flex flex-col gap-10'>
          <ReferCard />
          <HowItWorks />

          {/* FAQs visible only on xxl and above inside first column */}
          <div className='hidden xxl:block'>
            {faqLoading ? <SkeletonFAQItem /> : <FAQsSection faqs={faqs} />}
          </div>
        </div>

        {/* Right Column */}
        <div className='flex-[4] p-12'>
          <ReferralActions
            code={code}
            copied={copied}
            handleCopy={handleCopy}
            setCopied={setCopied}
          />
        </div>

        {/* FAQs visible only below entire layout for small screens */}
        <div className='block xxl:hidden p-12'>
          <FAQsSection faqs={faqs} />
        </div>
      </div>
    </motion.section>
  );
};

export default ReferAndEarn;
